import { del, get, patch, post } from "../util";

export type PersonPaalaegStatusId = "Aabent" | "Lukket" | "Slettet";

export type EfteruddannelseIndberetningStatusId =
  | "Ukendt"
  | "FristOrdinaer"
  | "ModtagetUdenFejl"
  | "ModtagetMedFejl"
  | "Undersoeges"
  | "IndbragtForAdvokatnaevnet"
  | "Henlagt";

export interface PersonPaalaegIndberetningLinjeDTO {
  id: string;
  efteruddannelsestypenr: number;
  indberettedeLektioner: number;
}

export interface PersonPaalaegIndberetningDTO {
  id: string;
  personPaalaegId: string;
  indberetningFrist?: string | null;
  registreringsDato?: string | null;
  signaturNavn?: string | null;
  efteruddannelseIndberetningStatus?: EfteruddannelseIndberetningStatusId | null;
  overfoertTilPersonEfteruddannelseId?: string | null;
  overfoertTilPersonEfteruddannelseIndberetningId?: string | null;
  lektionerOverfoert?: number | null;
  overfoertTilPeriodeFra?: string | null;
  overfoertTilPeriodeTil?: string | null;
  linjer: PersonPaalaegIndberetningLinjeDTO[];
}

export interface PersonPaalaegDTO {
  id: string;
  personId: string;
  paakraevedeLektioner: number;
  lektionerGennemfoert: number;
  lektionerOverfoert?: number | null;
  periodeFra: string;
  /** Periodens sidste dag, eller null hvis perioden endnu ikke er udløbet. */
  periodeTil: string | null;
  /** Indfrielsesperiodens længde i måneder, som afgørelsen fastsatte den. */
  periodeLaengdeMaaneder?: number | null;
  /** Står forløbet stille, fordi advokaten ikke er udøvende? */
  erIBero: boolean;
  /** Datoen forløbet gik i bero, hvis det står stille netop nu. */
  iBeroSiden?: string | null;
  /** Udøvende dage tilbage på det aktuelle trin — perioden eller fristen. */
  resterendeUdoevendeDage: number;
  kendelseDato?: string | null;
  /** Første kursusdato, der tæller som dokumentation. Null: indfrielsesperiodens start. */
  dokumentationGaelderFra?: string | null;
  bemaerkning?: string | null;
  status: PersonPaalaegStatusId;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  opretDato?: string | null;
  opretInit?: string | null;
  retteDato?: string | null;
  retteInit?: string | null;
  indberetning?: PersonPaalaegIndberetningDTO | null;
}

export interface CreatePersonPaalaegBody {
  paakraevedeLektioner: number;
  /** 6, 12 eller 18. Perioden og fristen beregnes af systemet. */
  periodeLaengdeMaaneder: number;
  kendelseDato: string;
  dokumentationGaelderFra?: string | null;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  bemaerkning?: string | null;
}

export interface UpdatePersonPaalaegFields {
  paakraevedeLektioner?: number;
  periodeLaengdeMaaneder?: number;
  kendelseDato?: string | Date | null;
  dokumentationGaelderFra?: string | null;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  bemaerkning?: string | null;
  efteruddannelseIndberetningStatus?: EfteruddannelseIndberetningStatusId | null;
}

export interface JsonPatchOperation {
  op: string;
  path: string;
  value?: unknown;
}

const paalaegBasePath = (personId: string) =>
  `person/${encodeURIComponent(personId)}/paalaeg`;

export const getPersonPaalaegList = (
  personId: string,
): Promise<PersonPaalaegDTO[]> =>
  get<PersonPaalaegDTO[]>({ query: paalaegBasePath(personId) });

export const getPersonPaalaeg = (
  personId: string,
  paalaegId: string,
): Promise<PersonPaalaegDTO> =>
  get<PersonPaalaegDTO>({
    query: `${paalaegBasePath(personId)}/${encodeURIComponent(paalaegId)}`,
  });

export const createPersonPaalaeg = (
  personId: string,
  body: CreatePersonPaalaegBody,
): Promise<PersonPaalaegDTO> =>
  post<PersonPaalaegDTO>({ query: paalaegBasePath(personId), body });

export const patchPersonPaalaeg = (
  personId: string,
  paalaegId: string,
  operations: JsonPatchOperation[],
): Promise<PersonPaalaegDTO> =>
  patch<PersonPaalaegDTO>({
    query: `${paalaegBasePath(personId)}/${encodeURIComponent(paalaegId)}`,
    body: operations,
  });

export const deletePersonPaalaeg = (
  personId: string,
  paalaegId: string,
): Promise<PersonPaalaegDTO> =>
  del<PersonPaalaegDTO>({
    query: `${paalaegBasePath(personId)}/${encodeURIComponent(paalaegId)}`,
  }).then((response) => response.data);
